<svelte:options tag={null} />

<script lang="ts">
  import {
    select,
    scaleBand,
    scaleLinear,
    axisBottom,
    axisLeft,
    Selection,
    ScaleBand,
    ScaleLinear,
  } from 'd3';
  import { onMount } from 'svelte';
  import type { BarData, BarKeyValue, BarMargin } from './bar-chart.types';
  import { EventHandler } from '../../utilities/EventHandler';

  export let data = '';
  let parsedData: BarData[];
  let subgroups: string[];
  $: parsedData = data ? (JSON.parse(data) as BarData[]) : [];
  $: groups = parsedData.map((d: BarData) => d.group);
  $: subgroups = parsedData[0]
    ? Object.keys(parsedData[0]).filter((d: string) => d !== 'group')
    : [];

  export let chartheight = '300';
  $: height = parseInt(chartheight, 10);

  export let backgroundcolor = '#fff';
  export let scalemax = 50;
  export let scalemin = 0;
  export let color = '#0B96CC';
  export let ticknumbers = 4;
  export let tickleftformat: (d: number, i: number) => string;
  tickleftformat = (d) => `${d}`;
  export let tickbottomformat: (d: number, i: number) => string = (d) => `${d}`;

  export let svgs: string;
  $: parsedSvgs = svgs ? (JSON.parse(svgs) as SVGElement[]) : [];

  export let margins: string;

  let _data: string;
  let chart: HTMLDivElement;
  let svg: Selection<SVGGElement, any, any, any>;
  let hasMounted = false;
  let tooltipData = { show: false, text: '', top: 0, left: 0, width: 0 };
  let eventHandler = new EventHandler();

  $: if (data.length > 0 && hasMounted) {
    if (!_data) {
      render();
      _data = data;
    } else if (data !== _data) {
      update();
      _data = data;
    }
  }

  function getParsedMargins(margins: string): BarMargin {
    if (margins) return JSON.parse(margins) as BarMargin;
    else
      return {
        top: 10,
        right: 40,
        bottom: 20,
        left: 60,
        xAxis: 20,
      } as BarMargin;
  }

  function getChartWidth() {
    let chartWidth = select(chart).style('width');
    if (chartWidth === '' || !chartWidth) {
      chartWidth = `${window.innerWidth / 2}`;
    }
    return parseInt(chartWidth);
  }

  function getBarKeyValue(d: BarData): BarKeyValue[] {
    return subgroups.map((key) => ({
      key: key,
      value: d[key].value,
      customtooltip: d[key].customtooltip,
    }));
  }

  function getAxes(width: number) {
    const x = scaleBand()
      .domain(groups)
      .range([
        0,
        width -
          (getParsedMargins(margins).left / 2 +
            getParsedMargins(margins).right / 2),
      ])
      .padding(0.2);
    const y = scaleLinear().domain([scalemin, scalemax]).range([height, 0]);
    const xSubgroup = scaleBand()
      .domain(subgroups)
      .range([0, x.bandwidth()])
      .padding(0.05);
    return { x, y, xSubgroup };
  }

  function addRectandles(
    x: ScaleBand<string>,
    xSubgroup: ScaleBand<string>,
    y: ScaleLinear<number, number, never>,
  ) {
    svg
      .append('g')
      .selectAll('g')
      .data(parsedData)
      .join('g')
      .attr('transform', (d) => `translate(${String(x(d.group))}, 0)`)
      .attr('class', 'bar')
      .selectAll('rect')
      .data((d) => getBarKeyValue(d))
      .join('rect')
      .attr('x', (d) => xSubgroup(d.key)!)
      .attr('y', () => y(0))
      .attr('width', xSubgroup.bandwidth())
      .attr('height', () => height - y(0))
      .style('fill', () => color)
      .style('cursor', 'pointer')
      .on('mouseout', (_e: MouseEvent, chartEvent: BarKeyValue) => {
        const others = svg.selectAll('rect').filter(function (arc) {
          return arc != chartEvent;
        });
        others.style('opacity', 1);
        tooltipData.show = false;
      })
      .on('mouseover', (e: MouseEvent, chartEvent: BarKeyValue) => {
        const others = svg.selectAll('rect').filter(function (arc) {
          return arc != chartEvent;
        });
        others.style('opacity', 0.7);
        const { left, top, width } = (
          e.target as HTMLElement
        ).getBoundingClientRect();
        tooltipData = {
          show: true,
          text: chartEvent.customtooltip
            ? `${chartEvent.customtooltip}`
            : `${chartEvent.key} - ${chartEvent.value}`,
          left: left,
          top: top - 50,
          width,
        };
      })
      .on('click', (_e: MouseEvent, chartEvent: BarKeyValue) => {
        eventHandler.dispatch('bar-clicked', chartEvent);
      });

    svg
      .selectAll<SVGElement, BarKeyValue>('rect')
      .transition()
      .duration(800)
      .attr('y', (d) => (+d.value < 0 ? y(0) : y(+d.value)))
      .attr('height', (d) =>
        +d.value < 0
          ? y(+d.value) - y(0)
          : height - y(+d.value + Number(scalemin)),
      )
      .delay((_d, i) => i * 100);
  }

  function updateAxes(
    x: ScaleBand<string>,
    y: ScaleLinear<number, number, never>,
  ) {
    svg
      .select('.x-axis')
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      .call((axisBottom(x) as any).tickFormat(tickbottomformat));
    svg
      .select('.y-axis')
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      .call((axisLeft(y) as any).ticks(ticknumbers).tickFormat(tickleftformat));

    const width = getChartWidth();
    svg
      .selectAll('.y-axis .tick line')
      .attr('stroke', '#EEEDEA')
      .attr('stroke-width', '1px')
      .attr('x2', width);
  }

  function render() {
    const width = getChartWidth();
    const { x, y, xSubgroup } = getAxes(width);

    svg = select<HTMLElement, any>(chart)
      .append('svg')
      .attr('width', width)
      .attr(
        'height',
        Number(height) +
          Number(getParsedMargins(margins).top) +
          Number(getParsedMargins(margins).bottom) +
          Number(getParsedMargins(margins).xAxis),
      )
      .attr('id', 'svg-chart')
      .append('g')
      .attr(
        'transform',
        `translate(${
          getParsedMargins(margins).left / 2 +
          getParsedMargins(margins).right / 2
        },${getParsedMargins(margins).top})`,
      );

    svg
      .append('g')
      .attr(
        'transform',
        `translate(0, ${
          Number(height) + Number(getParsedMargins(margins).xAxis)
        })`,
      )
      .call(axisBottom(x))
      .attr('class', 'x-axis')
      .attr('stroke-width', '0');

    svg
      .append('g')
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      .call((axisLeft(y) as any).ticks(ticknumbers).tickFormat(tickleftformat))
      .attr('class', 'y-axis')
      .attr('stroke-width', '0');

    addRectandles(x, xSubgroup, y);
  }

  function update() {
    const width = getChartWidth();
    chart.querySelector('#svg-chart')?.setAttribute('width', `${width}px`);

    const { x, y, xSubgroup } = getAxes(width);
    updateAxes(x, y);
    svg.selectAll('.bar').remove();
    addRectandles(x, xSubgroup, y);
  }

  function resize() {
    if (_data) {
      const width = getChartWidth();
      chart.querySelector('#svg-chart')?.setAttribute('width', `${width}px`);

      const { x, y, xSubgroup } = getAxes(width);
      updateAxes(x, y);

      svg
        .selectAll('.bar')
        .data(parsedData)
        .attr('transform', (d) => `translate(${Number(x(d.group))}, 0)`)
        .selectAll<SVGElement, BarKeyValue>('rect')
        .data((d) => getBarKeyValue(d))
        .attr('x', (d) => xSubgroup(d.key)!)
        .attr('width', xSubgroup.bandwidth());
    }
  }

  onMount(() => {
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(chart);
    hasMounted = true;
  });
</script>

<div bind:this={chart} style="background-color: {backgroundcolor};" />

{#if tooltipData.show}
  <div
    class="tooltip"
    style={`top: ${tooltipData.top}px; left: ${tooltipData.left}px; width: ${tooltipData.width}px;`}
  >
    {tooltipData.text}
  </div>
{/if}

<div style="height: 0; width: 0;" class="pattern">
  {#each parsedSvgs as svg}
    {@html svg}
  {/each}
</div>
<text />

<style lang="scss">
  @import '@mozaic-ds/styles/settings-tools/_all-settings';

  .tooltip {
    height: auto;
    background-color: $color-grey-700;
    border-radius: 4px;
    color: $color-grey-000;
    text-align: center;
    padding: magic-unit-rem(0.5) 0;
    position: absolute;
  }

  .pattern {
    z-index: -1;
    position: relative;
  }

  text {
    font-family: 'LeroyMerlin', sans-serif;
    font-size: $size-font-04;
    color: $color-grey-700;
  }
</style>
